/*
 * DOUBLE TAKE theme script (no dependencies, ~4 KB gz). Loaded with defer from snippets/dt-head.liquid.
 *  1. reveal: the second print arrives when a figure or heading scrolls in (only with html.dt-anim)
 *  2. hero drift: the echo lags behind the scroll
 *  3. reels: play / pause real videos, one at a time, pause out of view, progress bar
 *  4. look once / look twice slider
 *  5. fit pins
 *  6. cards: colour swatch selects in place (photo, name, price, links), no page change
 *  7. grid: filter chips, every product once per page, full rows only
 *  8. first look sign-up in two steps, copy code
 *  9. size guide: #size-guide opens it, cm / inches switch for "33-34 in, 84-86 cm" cells
 * 10. small things: complete-the-look sizes, drawer add-on sizes, FAQ deep links, sort select
 * Guards: html.dt-anim is only set without reduced motion and not on weak devices or in-app browsers
 * (snippets/factory-flags.liquid). On desktop Horizon scrolls .page-wrapper, not the window, so scroll
 * listeners are captured on document and positions come from getBoundingClientRect.
 */
(() => {
  'use strict';
  if (window.__dtReady) return;
  window.__dtReady = true;

  const $ = (s, r = document) => r.querySelector(s);
  const $$ = (s, r = document) => Array.from(r.querySelectorAll(s));
  const root = document.documentElement;
  const motion = () => root.classList.contains('dt-anim');

  /* ---------- 1. reveal ---------- */
  const ROW = '[data-dt-row]';
  let io = null;
  function reveal(scope) {
    const items = $$('[data-dt-reveal]:not(.is-in)', scope);
    if (!motion() || !('IntersectionObserver' in window)) {
      items.forEach((el) => el.classList.add('is-in'));
      return;
    }
    if (!io) {
      io = new IntersectionObserver(
        (entries) => {
          entries.forEach((e) => {
            if (!e.isIntersecting) return;
            const el = e.target;
            io.unobserve(el);
            let i = 0;
            const row = el.closest(ROW);
            if (row) {
              const kids = Array.from(row.children).filter((k) => !k.hidden);
              i = Math.max(0, kids.findIndex((k) => k.contains(el))) % 4;
            }
            setTimeout(() => el.classList.add('is-in'), i * 90);
          });
        },
        { rootMargin: '0px 0px -6% 0px', threshold: 0.12 }
      );
    }
    items.forEach((el) => io.observe(el));
  }

  /* ---------- 2. hero drift ---------- */
  let ticking = false;
  function drift() {
    ticking = false;
    $$('[data-dt-hero] .dt-hero__media').forEach((fig) => {
      const r = fig.getBoundingClientRect();
      const k = Math.min(1, Math.max(0, -r.top / (r.height || 1) + 0.15));
      fig.style.setProperty('--dt-drift', (k * 26).toFixed(1) + 'px');
    });
  }
  function onScroll() {
    if (ticking || !motion()) return;
    ticking = true;
    requestAnimationFrame(drift);
  }
  document.addEventListener('scroll', onScroll, { capture: true, passive: true });
  $$('[data-dt-hero]').forEach((hero) => {
    setTimeout(() => hero.classList.add('is-settled'), 1400);
  });

  /* ---------- 3. reels ---------- */
  function stopReel(reel) {
    reel.classList.remove('is-playing');
    const v = $('video', reel);
    if (v) v.pause();
    const b = $('[data-dt-play]', reel);
    if (b) b.setAttribute('aria-label', b.dataset.labelPlay || '');
  }
  function playReel(reel) {
    const tpl = $('template[data-dt-video]', reel);
    let v = $('video', reel);
    if (!v && tpl) {
      const frag = tpl.content.cloneNode(true);
      v = frag.querySelector('video');
      if (!v) return;
      v.muted = true;
      v.playsInline = true;
      v.loop = true;
      v.setAttribute('playsinline', '');
      v.classList.add('dt-reel__video');
      const poster = $('.dt-reel__poster', reel);
      poster.insertBefore(v, $('.dt-reel__place', poster) || $('.dt-reel__bar', poster));
      const bar = $('.dt-reel__bar i', reel);
      v.addEventListener('timeupdate', () => {
        if (bar && v.duration) bar.style.setProperty('--dt-p', (v.currentTime / v.duration).toFixed(3));
      });
    }
    if (!v) return;
    const p = v.play();
    if (p && p.catch) p.catch(() => stopReel(reel));
    reel.classList.add('is-playing');
    const b = $('[data-dt-play]', reel);
    if (b) b.setAttribute('aria-label', b.dataset.labelPause || '');
  }
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-dt-play]');
    if (!btn) return;
    const reel = btn.closest('[data-dt-reel]');
    const playing = reel.classList.contains('is-playing');
    $$('[data-dt-reel].is-playing').forEach(stopReel);
    if (!playing) playReel(reel);
  });
  if ('IntersectionObserver' in window) {
    const reelIo = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (!e.isIntersecting && e.target.classList.contains('is-playing')) stopReel(e.target);
      });
    });
    $$('[data-dt-reel]').forEach((r) => reelIo.observe(r));
  }

  /* ---------- 4. look once / look twice ---------- */
  function stages(scope) {
    $$('[data-dt-stage]', scope).forEach((stage) => {
      const r = $('input[type="range"]', stage);
      if (!r || r._dt) return;
      r._dt = true;
      const set = () => stage.style.setProperty('--dt-pos', r.value + '%');
      r.addEventListener('input', set);
      set();
    });
  }

  /* ---------- 5. fit pins ---------- */
  function pins(scope) {
    $$('[data-dt-pins]', scope).forEach((sec) => {
      if (sec._dt) return;
      sec._dt = true;
      const clear = () => $$('.dt-pin.is-on, .dt-fit__item.is-on', sec).forEach((x) => x.classList.remove('is-on'));
      const on = (n) => {
        clear();
        if (!n) return;
        $$('[data-dt-pin="' + n + '"], [data-dt-pin-item="' + n + '"]', sec).forEach((x) => x.classList.add('is-on'));
      };
      $$('[data-dt-pin]', sec).forEach((p) => {
        const n = p.dataset.dtPin;
        p.addEventListener('mouseenter', () => on(n));
        p.addEventListener('mouseleave', () => on(null));
        p.addEventListener('focus', () => on(n));
        p.addEventListener('blur', () => on(null));
        p.addEventListener('click', () => on(p.classList.contains('is-on') ? null : n));
      });
      $$('[data-dt-pin-item]', sec).forEach((item) => {
        const n = item.dataset.dtPinItem;
        item.addEventListener('mouseenter', () => on(n));
        item.addEventListener('mouseleave', () => on(null));
      });
    });
  }

  /* ---------- 6. cards: swatch selects the colour in place ---------- */
  document.addEventListener('pointerover', (e) => {
    const sw = e.target.closest && e.target.closest('.dt-swatch');
    if (!sw || sw._pre) return;
    sw._pre = true;
    [sw.dataset.src, sw.dataset.src2].filter(Boolean).forEach((u) => {
      const i = new Image();
      i.src = u;
    });
  });
  document.addEventListener('click', (e) => {
    const sw = e.target.closest('.dt-swatch');
    if (!sw) return;
    const card = sw.closest('[data-dt-card]');
    if (!card) return;
    e.preventDefault();
    $$('.dt-swatch', card).forEach((s) => s.setAttribute('aria-pressed', String(s === sw)));
    const title = ($('.dt-card__title', card) || {}).textContent || '';
    [['1', sw.dataset.src, sw.dataset.srcset], ['2', sw.dataset.src2, sw.dataset.srcset2]].forEach(([n, src, srcset]) => {
      const img = $('[data-dt-img="' + n + '"]', card);
      if (!img || !src) return;
      if (srcset) img.srcset = srcset;
      img.src = src;
      img.alt = n === '1' ? title.trim() + ', ' + sw.dataset.name : '';
    });
    const name = $('[data-dt-color-name]', card);
    if (name) name.textContent = sw.dataset.name || '';
    const price = $('[data-dt-price]', card);
    if (price && sw.dataset.price && !price.querySelector('s')) price.textContent = sw.dataset.price;
    if (sw.dataset.url) $$('[data-dt-card-link]', card).forEach((a) => a.setAttribute('href', sw.dataset.url));
  });

  /* ---------- 7. grid: chips, dedupe, full rows ---------- */
  const seen = new Set();
  function dedupe() {
    $$('[data-dt-dedupe]').forEach((sec) => {
      if (sec._dtDone) return;
      const cards = $$('[data-dt-card]', sec);
      cards.forEach((c) => {
        const k = c.dataset.dtKey;
        if (seen.has(k)) {
          c.hidden = true;
          c.dataset.dtDupe = '1';
        } else seen.add(k);
      });
      sec._dtDone = true;
      const limit = Number(sec.dataset.dtLimit) || 0;
      let shown = 0;
      cards.forEach((c) => {
        if (c.dataset.dtDupe) return;
        shown += 1;
        if (limit && shown > limit) {
          c.hidden = true;
          c.dataset.dtSpare = '1';
        }
      });
      if (!cards.some((c) => !c.hidden)) sec.hidden = true;
    });
  }
  function cols(grid) {
    const t = getComputedStyle(grid).gridTemplateColumns;
    return t && t !== 'none' ? t.split(' ').filter(Boolean).length : 1;
  }
  function fullRows(grid) {
    if (!grid.classList.contains('dt-grid')) return;
    const cards = $$('[data-dt-card]', grid).filter((c) => !c.dataset.dtDupe && !c.dataset.dtSpare && !c.dataset.dtFiltered);
    cards.forEach((c) => {
      if (c.dataset.dtTrim) {
        delete c.dataset.dtTrim;
        c.hidden = false;
      }
    });
    const n = cols(grid);
    if (n < 2) return;
    const cells = cards.reduce((sum, c) => sum + (c.classList.contains('dt-card--wide') ? 2 : 1), 0);
    const extra = cells % n;
    if (cells <= n || extra === 0 || extra > Math.ceil(n / 2)) return;
    let cut = extra;
    for (let i = cards.length - 1; i >= 0 && cut > 0; i -= 1) {
      if (cards[i].classList.contains('dt-card--wide')) break;
      cards[i].hidden = true;
      cards[i].dataset.dtTrim = '1';
      cut -= 1;
    }
  }
  function filter(sec, handle) {
    $$('[data-dt-filter]', sec).forEach((ch) => ch.setAttribute('aria-pressed', String(ch.dataset.dtFilter === handle)));
    $$('[data-dt-card]', sec).forEach((c) => {
      if (c.dataset.dtDupe) return;
      const match = handle === 'all' || (c.dataset.collections || '').indexOf(' ' + handle + ' ') > -1;
      if (handle === 'all') {
        delete c.dataset.dtFiltered;
        c.hidden = Boolean(c.dataset.dtSpare);
      } else {
        c.hidden = !match;
        if (match) delete c.dataset.dtFiltered;
        else c.dataset.dtFiltered = '1';
        delete c.dataset.dtTrim;
      }
    });
    const grid = $('[data-dt-grid]', sec);
    if (grid && handle === 'all') fullRows(grid);
    $$('[data-dt-reveal]:not(.is-in)', sec).forEach((el) => el.classList.add('is-in'));
  }
  document.addEventListener('click', (e) => {
    const ch = e.target.closest('[data-dt-filter]');
    if (!ch) return;
    filter(ch.closest('[data-dt-shop]'), ch.dataset.dtFilter);
  });
  let rt;
  window.addEventListener('resize', () => {
    clearTimeout(rt);
    rt = setTimeout(() => {
      $$('[data-dt-shop]').forEach((sec) => {
        const pressed = $('[data-dt-filter][aria-pressed="true"]', sec);
        if (!pressed || pressed.dataset.dtFilter === 'all') {
          const grid = $('[data-dt-grid]', sec);
          if (grid) fullRows(grid);
        }
      });
    }, 150);
  });

  /* ---------- 8. first look sign-up ---------- */
  function signup(scope) {
    $$('form[data-dt-signup]', scope).forEach((form) => {
      if (form._dt) return;
      form._dt = true;
      const step = (n) => $$('[data-dt-step]', form).forEach((s) => { s.hidden = s.dataset.dtStep !== String(n); });
      const email = $('input[type="email"]', form);
      const err = $('[data-dt-err]', form);
      const tags = $('[data-dt-tags]', form);
      let chosen = '';
      form.addEventListener('submit', (e) => {
        const onStep1 = $('[data-dt-step="1"]', form) && !$('[data-dt-step="1"]', form).hidden;
        if (!onStep1 || !$('[data-dt-step="2"]', form)) return;
        e.preventDefault();
        const v = (email.value || '').trim();
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v)) {
          if (err) err.textContent = err.dataset.message || '';
          email.setAttribute('aria-invalid', 'true');
          email.focus();
          return;
        }
        if (err) err.textContent = '';
        email.removeAttribute('aria-invalid');
        step(2);
        const first = $('[data-dt-step="2"] button', form);
        if (first) first.focus();
      });
      form.addEventListener('click', (e) => {
        const chip = e.target.closest('[data-dt-tag]');
        if (chip) {
          const on = chip.getAttribute('aria-pressed') !== 'true';
          $$('[data-dt-tag]', form).forEach((c) => c.setAttribute('aria-pressed', 'false'));
          chip.setAttribute('aria-pressed', String(on));
          chosen = on ? chip.dataset.dtTag : '';
          if (tags) tags.value = [tags.dataset.base, chosen].filter(Boolean).join(', ');
          return;
        }
        const fin = e.target.closest('[data-dt-finish]');
        if (fin && fin.classList.contains('dt-textbtn') && tags) tags.value = tags.dataset.base || '';
      });
      if ($('[data-dt-done]', form)) {
        const sec = form.closest('.dt-first') || form;
        setTimeout(() => sec.scrollIntoView({ block: 'center' }), 60);
      }
    });
  }
  document.addEventListener('click', (e) => {
    const b = e.target.closest('[data-dt-copy]');
    if (!b) return;
    const done = () => {
      const was = b.textContent;
      b.textContent = b.dataset.done || was;
      setTimeout(() => { b.textContent = was; }, 1800);
    };
    if (navigator.clipboard && navigator.clipboard.writeText) navigator.clipboard.writeText(b.dataset.dtCopy).then(done, done);
    else done();
  });

  /* ---------- 9. size guide ---------- */
  function sizeGuides(scope) {
    $$('[data-dt-size-guide]', scope).forEach((panel) => {
      if (panel._dt) return;
      panel._dt = true;
      const chart = $('[data-dt-chart]', panel);
      const units = $('[data-dt-units]', panel);
      if (!chart || !units) return;
      const re = /^\s*([\d.,\s-]+)\s*in\s*,\s*([\d.,\s-]+)\s*cm\s*$/i;
      const cells = $$('td', chart).filter((td) => re.test(td.textContent));
      if (!cells.length) return;
      cells.forEach((td) => {
        const m = td.textContent.match(re);
        td.dataset.in = m[1].trim() + ' in';
        td.dataset.cm = m[2].trim() + ' cm';
        td.dataset.both = td.textContent.trim();
      });
      units.hidden = false;
      const pick = (b) => {
        const u = b.dataset.dtUnit;
        $$('[data-dt-unit]', units).forEach((x) => x.setAttribute('aria-pressed', String(x === b)));
        cells.forEach((td) => { td.textContent = td.dataset[u]; });
      };
      units.addEventListener('click', (e) => {
        const b = e.target.closest('[data-dt-unit]');
        if (b) pick(b);
      });
      /* inches for US English browsers, centimetres everywhere else; "both" stays one tap away */
      const us = /-US$/i.test(navigator.language || '');
      const first = $('[data-dt-unit="' + (us ? 'in' : 'cm') + '"]', units);
      if (first) pick(first);
    });
  }
  function openSizeGuide() {
    const panel = $('[data-dt-size-guide]');
    if (panel && panel.showPopover) {
      try { panel.showPopover(); } catch (e) { /* already open */ }
      return true;
    }
    return false;
  }
  document.addEventListener('click', (e) => {
    const a = e.target.closest('a[href$="#size-guide"]');
    if (!a) return;
    const url = new URL(a.href, location.href);
    if (url.pathname === location.pathname && openSizeGuide()) e.preventDefault();
  });

  /* ---------- 10. small things ---------- */
  document.addEventListener('click', (e) => {
    const b = e.target.closest('[data-dt-ctl-size]');
    if (!b || b.disabled) return;
    const card = b.closest('[data-dt-ctl]');
    $$('[data-dt-ctl-size]', card).forEach((x) => x.setAttribute('aria-pressed', String(x === b)));
    const add = $('factory-add-button', card);
    if (add) add.dataset.variantId = b.dataset.variantId;
  });
  document.addEventListener('change', (e) => {
    const sel = e.target.closest && e.target.closest('select[data-dt-variant-select]');
    if (sel) {
      const row = sel.closest('[data-factory-upsell-row]');
      const add = row && $('factory-add-button', row);
      if (add) add.dataset.variantId = sel.value;
      return;
    }
    const sort = e.target.closest && e.target.closest('form[data-dt-sort] select');
    if (sort) sort.form.submit();
  });
  function openFaq() {
    const h = decodeURIComponent(location.hash.slice(1));
    if (!h) return;
    if (h === 'size-guide') {
      setTimeout(openSizeGuide, 250);
      return;
    }
    const d = document.getElementById(h);
    if (d && d.tagName === 'DETAILS') {
      d.open = true;
      setTimeout(() => d.scrollIntoView({ block: 'start' }), 60);
    }
  }
  window.addEventListener('hashchange', openFaq);

  /* complete the look: swap in Search & Discovery pairs when Shopify has them */
  function complete(scope) {
    $$('[data-dt-complete][data-url]', scope).forEach((sec) => {
      if (sec._dt) return;
      sec._dt = true;
      fetch(sec.dataset.url, { credentials: 'same-origin' })
        .then((r) => (r.ok ? r.text() : ''))
        .then((html) => {
          if (!html) return;
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const list = doc.querySelector('[data-dt-ctl-list][data-source="rec"]');
          const target = $('[data-dt-ctl-list]', sec);
          if (!list || !target || !list.children.length) return;
          target.replaceChildren(...Array.from(list.children));
        })
        .catch(() => {});
    });
  }

  function init(scope) {
    reveal(scope);
    stages(scope);
    pins(scope);
    signup(scope);
    sizeGuides(scope);
    complete(scope);
  }
  init(document);
  dedupe();
  $$('[data-dt-shop] [data-dt-grid]').forEach(fullRows);
  drift();
  openFaq();

  /* theme editor: sections re-render in place */
  document.addEventListener('shopify:section:load', (e) => {
    init(e.target);
    $$('[data-dt-reveal]', e.target).forEach((el) => el.classList.add('is-in'));
    $$('[data-dt-grid]', e.target).forEach(fullRows);
  });
})();
